'use client'

import React, { useEffect, useRef, useState } from 'react'
import styles from './styles.module.css'
import { useRegionOffers } from '../../../RegionOffers'
import { useRegionCountryCode } from '../../../RegionGeo'
import { useOffersStore } from '../../../../store/offersStore'
import { useCartStore, selectCart } from '../../../../store/cartStore'
import { useSessionStore } from '../../../../store/sessionStore'
import AddressInput from './AddressInput'
import PaymentInput from './PaymentInput'
import InvoicePreview from './InvoicePreview'
import Text from '../../../Text'
import CtaV2 from '../../../CtaV2'
import InvoicePreviewWithoutOrder from './InvoicePreviewWithoutOrder'
import { useInvoicePreviewQuote } from './InvoicePreviewWithoutOrder/useInvoicePreviewQuote'
import { PREVIEW_FAILURE_SLOT } from './InvoicePreviewWithoutOrder/previewFailure'
import sendMetaPixelEvent, { buildInitiateCheckoutEvent } from '../../../../utils/metaPixel'
import { calculateShoppingCartTotal } from '../../../../utils/shopItemsAndCart'
import { pushEcommerceEvent, buildEcommercePayload } from '../../../../utils/dataLayer'
import { getAutoDiscountCode } from '../../../../lib/expoPromo'

function PaymentPage({ showDiscountCodeOption }) {
  // Cart line items now come from the Zustand cart store (Bucket C); paymentStartedInfo
  // from the Zustand session store (Phase C2 part 1). Offers moved to the RegionOffers
  // seam (Phase D1) — the grid offers threaded to this checkout region page resolve
  // each cart id to its item/price.
  const shoppingCart = useCartStore(selectCart)
  const paymentStartedInfo = useSessionStore((s) => s.paymentStartedInfo)
  const { offers, offersFetched } = useRegionOffers()
  const region = useRegionCountryCode()
  const ensureFullOffers = useOffersStore((s) => s.ensureFullOffers)

  const [forceShowAddressInput, setForceShowAddressInput] = useState(false)

  // Direct /checkout loads (window.open('/checkout','_self') from the cart panel)
  // don't fetch offers themselves — the shop/product pages do it upstream. When a
  // returning visitor lands straight on /checkout with a cart from localStorage,
  // no upstream fetch ever ran, so offersFetched stays false and the
  // begin_checkout event below never fires (Jahangir's #2 regression). Mirror
  // the SpecsPage / four checkout forks pattern: trigger ensureFullOffers on
  // mount. Idempotent — noops when offers for this region are already loaded.
  useEffect(() => {
    if (!offersFetched) {
      ensureFullOffers(region)
    }
  }, [region])

  // Fire begin_checkout ONCE per page load, but only AFTER both:
  //   - shoppingCart has hydrated from localStorage (appRendered ->
  //     setShoppingCartFromLocalStorage runs post-render, so on first mount the
  //     cart is empty even for a returning visitor with items)
  //   - shopItems has fetched (needed to resolve each cart id -> item for value
  //     + items[] enrichment)
  // The prior [] dep-array fired on mount before either was ready, giving
  // Jahangir the empty-payload event he flagged.
  const beginCheckoutSent = useRef(false)
  useEffect(() => {
    if (beginCheckoutSent.current) return
    const cartIds = Object.keys(shoppingCart || {})
    if (cartIds.length === 0) return
    if (!offersFetched || !offers || !offers.length) return
    const cartEntries = cartIds
      .map(id => ({ item: offers.find(i => i.id === id), quantity: shoppingCart[id] }))
      .filter(entry => entry.item)
    if (!cartEntries.length) return
    beginCheckoutSent.current = true
    const cartTotal = calculateShoppingCartTotal(offers, shoppingCart)
    sendMetaPixelEvent(buildInitiateCheckoutEvent(cartIds, cartTotal))
    pushEcommerceEvent('begin_checkout', buildEcommercePayload(cartEntries))
  }, [shoppingCart, offersFetched, offers])

  if (Object.keys(shoppingCart).length === 0) {
    return <div className={styles.emptyShoppingCartWrapper}>
        <Text type="heading-3">Your shopping cart is empty</Text>
        <div>
          <Text type="large">{"You don't have any products in the cart yet."}</Text>
          <Text type="large">{'You will find our products on our "Shop" page.'}</Text>
        </div>
        <CtaV2
          title={'Go to shop'}
          dark
          onClick={() => window.open('/shop', '_self')}
        />
      </div>
  }

  return (
    <CheckoutSteps
      paymentStartedInfo={paymentStartedInfo}
      forceShowAddressInput={forceShowAddressInput}
      setForceShowAddressInput={setForceShowAddressInput}
      showDiscountCodeOption={showDiscountCodeOption}
    />
  )
}

// The checkout proper, mounted only once the cart has hydrated non-empty (so the
// session store — the saved discount code — has hydrated too; both hydrators run in
// SiteChrome's mount effects, before the cart can be non-empty).
//
// Owns the pre-order invoice preview: ONE quote (useInvoicePreviewQuote) shared by
// the two summary slots below — the mobile slot between the address and payment
// steps and the desktop slot in the side column. CSS shows one of them; mounting a
// self-fetching component in each would fire every backend call twice.
function CheckoutSteps({ paymentStartedInfo, forceShowAddressInput, setForceShowAddressInput, showDiscountCodeOption }) {
  // The code the quote is priced with: the customer's last Apply, else the code
  // the session carried in (a ?discount-code entry / an earlier eligible Apply).
  // Seeded ONCE on mount on purpose: when the backend refuses the code the hook
  // clears it from the session, and following the session here would re-quote
  // without the code and wipe the very warning that explains the refusal.
  const [discountCode, setDiscountCode] = useState(() => useSessionStore.getState().discountCode || getAutoDiscountCode() || null)

  const orderInvoice = paymentStartedInfo && paymentStartedInfo.invoice ? paymentStartedInfo.invoice : null
  const quote = useInvoicePreviewQuote({ discountCode, enabled: !orderInvoice })

  const getInvoicePreview = () => {
    return orderInvoice
      ? <InvoicePreview invoice={orderInvoice}/>
      : <InvoicePreviewWithoutOrder
          quote={quote}
          discountCode={discountCode}
          onDiscountCodeSubmit={(value) => setDiscountCode(value || null)}
          showDiscountCodeOption={showDiscountCodeOption}
        />
  }

  // A region-class refusal (the country does not sell / we do not ship there) is
  // ALSO shown under the shipping-country picker — that is the control that fixes it.
  const regionFailure = !orderInvoice && quote.failure && quote.failure.slot === PREVIEW_FAILURE_SLOT.REGION
    ? quote.failure
    : null

  const showAddressInput = !paymentStartedInfo || forceShowAddressInput

  return (
    <div className={styles.wrapper}>
      <div className={styles.contentWrapper}>
        <div className={styles.checkoutProcess}>
          <div className={styles.addressInputWrapper}>
            <AddressInput
              title="Shipping"
              subtitle="Shipping address"
              stepNumber={1}
              expanded={showAddressInput}
              onSubmit={() => setForceShowAddressInput(false)}
              regionFailure={regionFailure}
            />
            { !showAddressInput && <div className={styles.editButton} onClick={() => setForceShowAddressInput(true)}>
                <Text type="regular">Edit</Text>
              </div>
            }
          </div>
          <div className={`${styles.invoicePreviewWrapper} ${styles.invoicePreviewWrapperMobileOnly}`}>
            { getInvoicePreview() }
          </div>
          <PaymentInput
            title="Payment"
            subtitle=""
            stepNumber={2}
            expanded={!showAddressInput}
          />
        </div>
        <div className={`${styles.invoicePreviewWrapper} ${styles.invoicePreviewWrapperDesktopOnly}`}>
          { getInvoicePreview() }
        </div>
      </div>
    </div>
  )
}

export default PaymentPage
